import { useCallback, useEffect, useRef, useState } from 'react';

const validRevision = value => typeof value === 'string'
  && /^(0|[1-9][0-9]{0,18})$/.test(value)
  && BigInt(value) <= 9223372036854775807n;

export default function useStorePlantAuthoring(clienteId, onLoaded) {
  const [state, setState] = useState({ phase: 'LOADING', revision: null, error: '' });
  const snapshot = useRef({ tenant: null, revision: null, ready: false });
  const sequence = useRef(0);
  const controller = useRef(null);
  const saveInProgress = useRef(false);
  const applyLoaded = useRef(onLoaded);
  applyLoaded.current = onLoaded;

  const load = useCallback(async () => {
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    const token = ++sequence.current;
    const timer = setTimeout(() => abort.abort(), 15000);
    snapshot.current = { tenant: clienteId, revision: null, ready: false };
    setState({ phase: 'LOADING', revision: null, error: '' });
    try {
      if (!clienteId) throw new Error('Cliente não identificado.');
      const response = await fetch(`/api/store-map/${encodeURIComponent(clienteId)}/planta`, { signal: abort.signal });
      const data = await response.json();
      if (!response.ok || data.ok === false) throw new Error(data.erro || 'Não foi possível carregar a planta.');
      if (data.authoringIntegrity !== 'CANONICAL_CAS' || !validRevision(data.authoringRevision)) {
        throw new Error('A proteção de edição da planta está indisponível. Atualize o backend e a migração antes de editar.');
      }
      if (!['perimetro', 'elementos', 'secoes', 'rota'].every(key => Array.isArray(data[key]))) {
        throw new Error('Resposta de planta inválida. Os dados atuais foram preservados.');
      }
      if (token !== sequence.current) return;
      if (abort.signal.aborted) throw new Error('Tempo de carregamento excedido.');
      applyLoaded.current(data);
      snapshot.current = { tenant: clienteId, revision: data.authoringRevision, ready: true };
      setState({ phase: 'LOADED', revision: data.authoringRevision, error: '' });
    } catch (error) {
      if (token !== sequence.current) return;
      setState({ phase: 'LOAD_FAILED', revision: null, error: abort.signal.aborted
        ? 'Tempo de carregamento excedido. Tente novamente.' : error.message || 'Falha ao carregar a planta.' });
    } finally { clearTimeout(timer); }
  }, [clienteId]);

  useEffect(() => {
    load();
    return () => { ++sequence.current; controller.current?.abort(); snapshot.current.ready = false; };
  }, [load]);

  const commit = useCallback(async payload => {
    if (saveInProgress.current || !snapshot.current.ready || snapshot.current.tenant !== clienteId) {
      throw new Error('Carregue a revisão canônica antes de salvar.');
    }
    const token = sequence.current;
    const revision = snapshot.current.revision;
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 30000);
    saveInProgress.current = true;
    setState({ phase: 'SAVING', revision, error: '' });
    try {
      const response = await fetch(`/api/store-map/${encodeURIComponent(clienteId)}/planta`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        signal: abort.signal,
        body: JSON.stringify({ ...payload, expectedRevision: revision }),
      });
      const data = await response.json();
      if (token !== sequence.current) {
        const error = new Error('A sessão de edição mudou durante o salvamento.');
        error.code = 'AUTHORING_SESSION_CHANGED';
        throw error;
      }
      if (abort.signal.aborted) throw new Error('Tempo de salvamento excedido. Recarregue para conferir a revisão canônica.');
      if (!response.ok || !data.ok) {
        const stale = data.code === 'STALE_REVISION';
        if (stale) snapshot.current.ready = false;
        setState({ phase: stale ? 'STALE_REVISION' : 'SAVE_FAILED', revision,
          error: data.erro || 'Falha ao salvar. O rascunho foi preservado.' });
        return { response, data };
      }
      if (data.authoringIntegrity !== 'CANONICAL_CAS' || !validRevision(data.authoringRevision)
        || BigInt(data.authoringRevision) < BigInt(revision) || !data.planta
        || !['perimetro', 'elementos', 'secoes', 'rota'].every(key => Array.isArray(data.planta[key]))) {
        snapshot.current.ready = false;
        throw new Error('Salvamento não confirmado por revisão canônica. Preserve o rascunho e recarregue a planta.');
      }
      snapshot.current.revision = data.authoringRevision;
      setState({ phase: 'SAVED', revision: data.authoringRevision, error: '' });
      return { response, data };
    } catch (error) {
      if (token === sequence.current) {
        setState({ phase: 'SAVE_FAILED', revision, error: error.message || 'Falha ao salvar. O rascunho foi preservado.' });
      }
      throw error;
    } finally { clearTimeout(timer); saveInProgress.current = false; }
  }, [clienteId]);

  return { ...state, load, commit,
    editable: snapshot.current.tenant === clienteId && snapshot.current.ready && state.phase !== 'SAVING' };
}
